<template>
  <AdminLayout>
    <div class="config-management">
      <h2>BioRender管理员账户配置</h2>

      <!-- 添加新管理员账户 -->
      <section class="add-admin-section">
        <h3>添加新管理员账户</h3>

        <!-- 批量导入区域 -->
        <div class="bulk-import-section">
          <div class="import-actions">
            <div class="import-buttons-row">
              <button
                type="button"
                @click="$refs.fileInput.click()"
                class="btn btn-primary"
                :disabled="loading"
              >
                📁 Excel批量导入
              </button>
              <button
                type="button"
                @click="downloadTemplate"
                class="btn btn-secondary"
              >
                📄 下载模板
              </button>
            </div>
            <div class="import-buttons-row">
              <button
                type="button"
                @click="exportAccounts"
                class="btn btn-success"
                :disabled="adminAccounts.length === 0"
              >
                📤 导出账户
              </button>
            </div>
          </div>
          <p class="form-hint">支持 .xlsx 和 .xls 格式，模板包含：邮箱、密码两列</p>
          <input
            type="file"
            ref="fileInput"
            @change="handleFileUpload"
            accept=".xlsx,.xls"
            style="display: none"
          />
        </div>

        <!-- 单个添加表单 -->
        <form @submit.prevent="addAdmin" class="add-admin-form">
          <div class="flex gap-lg align-items-end">
            <div class="form-group" style="flex: 1;">
              <label>管理员邮箱</label>
              <input
                type="email"
                v-model="newAdmin.email"
                placeholder="请输入BioRender管理员邮箱"
                class="form-control"
                required
              />
            </div>
            <div class="form-group" style="flex: 1;">
              <label>管理员密码</label>
              <div class="password-input">
                <input
                  :type="showNewPassword ? 'text' : 'password'"
                  v-model="newAdmin.password"
                  placeholder="请输入BioRender管理员密码"
                  class="form-control"
                  required
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click="showNewPassword = !showNewPassword"
                >
                  {{ showNewPassword ? '👁️' : '👁️‍🗨️' }}
                </button>
              </div>
            </div>
            <button
              type="submit"
              :disabled="!newAdmin.email || !newAdmin.password || loading"
              class="btn btn-success"
            >
              添加账户
            </button>
          </div>
        </form>
      </section>

      <!-- 管理员账户列表 -->
      <section class="admin-accounts-section">
        <div class="section-header flex justify-content-between align-items-center">
          <h3>管理员账户列表</h3>
          <div class="list-controls flex gap-lg align-items-center">
            <button
              type="button"
              @click="loadConfig"
              class="btn btn-info btn-sm"
              :disabled="loading"
            >
              🔄 刷新
            </button>
            <div class="batch-operations flex gap-sm">
              <button
                type="button"
                @click="selectAllAccounts"
                class="btn btn-secondary btn-sm"
                :disabled="filteredAccounts.length === 0"
              >
                {{ allSelected ? '取消全选' : '全选' }}
              </button>
              <button
                type="button"
                @click="deleteSelected"
                class="btn btn-danger btn-sm"
                :disabled="selectedAccounts.length === 0"
              >
                批量删除 ({{ selectedAccounts.length }})
              </button>
            </div>
            <div class="search-box">
              <input
                type="text"
                v-model="searchKeyword"
                placeholder="搜索邮箱..."
                class="form-control"
                style="width: 200px;"
              />
            </div>
          </div>
        </div>

        <!-- 移动端卡片式布局 -->
        <div class="mobile-accounts" v-if="isMobile">
          <div v-if="loading" class="mobile-loading">
            正在加载管理员账户数据...
          </div>
          <div v-else-if="adminAccounts.length === 0" class="mobile-empty">
            <p v-if="totalAccounts === 0">暂无管理员账户数据</p>
            <p v-else>第 {{ currentPage }} 页暂无数据</p>
          </div>
          <div v-else class="mobile-account-list">
            <div v-for="(item, index) in adminAccounts" :key="getAccountId(item, index)" :class="['mobile-account-item', { 'highlighted': item.isActive }]">
              <div class="mobile-account-header">
                <div class="account-email">{{ item.email }}</div>
                <span class="status-badge" :class="getStatusClass(item.status)">
                  {{ getAccountStatusText(item.status) }}
                </span>
              </div>
              <div class="mobile-account-content">
                <div class="mobile-account-row">
                  <span class="label">密码:</span>
                  <div class="password-display">
                    <input
                      :type="item.showPassword ? 'text' : 'password'"
                      :value="item.password"
                      readonly
                      class="password-input-display"
                    />
                    <button
                      type="button"
                      class="password-toggle-mobile"
                      @click="togglePassword(index)"
                    >
                      {{ item.showPassword ? '👁️' : '👁️‍🗨️' }}
                    </button>
                  </div>
                </div>
                <div class="mobile-account-row">
                  <span class="label">已激活用户:</span>
                  <span class="user-count" :class="{ 'full': (item.activatedUsersCount || 0) >= 9 }">
                    {{ item.activatedUsersCount || 0 }}/9
                  </span>
                </div>
                <div class="mobile-account-row">
                  <span class="label">最后使用:</span>
                  <span>{{ item.lastUsed || '未使用' }}</span>
                </div>
                <div class="mobile-account-row" v-if="!item.isActive">
                  <input
                    type="checkbox"
                    v-model="selectedAccounts"
                    :value="getAccountId(item, index)"
                    :disabled="item.isActive"
                  />
                  <span class="checkbox-label">选择此账户</span>
                </div>
              </div>
              <div class="mobile-account-actions">
                <button
                  v-if="!item.isActive && getStatusClass(item.status) === 'status-available'"
                  @click="setActiveAccount(getGlobalIndex(index))"
                  class="btn btn-primary btn-sm"
                >
                  设为当前
                </button>
                <button
                  @click="removeAdmin(getGlobalIndex(index))"
                  class="btn btn-danger btn-sm"
                  :disabled="item.isActive"
                >
                  删除
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 桌面端表格布局 -->
        <AdminTable
          v-else
          :columns="tableColumns"
          :data="adminAccounts"
          :total="totalAccounts"
          :current-page="currentPage"
          :page-size="pageSize"
          :row-key="getAccountId"
          :row-class-name="getRowClassName"
          max-height="500px"
        >
          <template #checkbox="{ item, index }">
            <input
              type="checkbox"
              v-model="selectedAccounts"
              :value="getAccountId(item, index)"
              :disabled="item.isActive"
            />
          </template>

          <template #password="{ item, index }">
            <div class="password-display flex align-items-center gap-sm">
              <input
                :type="item.showPassword ? 'text' : 'password'"
                :value="item.password"
                readonly
                class="password-input-display"
              />
              <button
                type="button"
                class="password-toggle-table"
                @click="togglePassword(index)"
              >
                {{ item.showPassword ? '👁️' : '👁️‍🗨️' }}
              </button>
            </div>
          </template>

          <template #activatedUsers="{ item }">
            <span class="user-count" :class="{ 'full': (item.activatedUsersCount || 0) >= 9 }">
              {{ item.activatedUsersCount || 0 }}/9
            </span>
          </template>

          <template #status="{ item }">
            <span class="status-badge" :class="getStatusClass(item.status)">
              {{ getAccountStatusText(item.status) }}
            </span>
          </template>

          <template #lastUsed="{ item }">
            {{ item.lastUsed || '未使用' }}
          </template>

          <template #actions="{ item, index }">
            <div class="flex gap-sm">
              <button
                v-if="!item.isActive && getStatusClass(item.status) === 'status-available'"
                @click="setActiveAccount(getGlobalIndex(index))"
                class="btn btn-primary btn-sm"
              >
                设为当前
              </button>
              <button
                @click="removeAdmin(getGlobalIndex(index))"
                class="btn btn-danger btn-sm"
                :disabled="item.isActive"
              >
                删除
              </button>
            </div>
          </template>
        </AdminTable>

        <AdminPagination
          :current-page="currentPage"
          :page-size="pageSize"
          :total="totalAccounts"
          @update:currentPage="currentPage = $event; loadConfig()"
          @update:pageSize="pageSize = $event; currentPage = 1; loadConfig()"
        />
      </section>

      <!-- 当前激活账户信息 -->
      <section class="current-account-info" v-if="currentActiveAccount">
        <h3>当前激活账户</h3>
        <div class="current-account-card">
          <div class="account-info flex gap-xl">
            <div class="info-item">
              <label>邮箱:</label>
              <span>{{ currentActiveAccount.email }}</span>
            </div>
            <div class="info-item">
              <label>已激活用户:</label>
              <span class="user-count" :class="{ 'full': (currentActiveAccount.activatedUsersCount || 0) >= 9 }">
                {{ currentActiveAccount.activatedUsersCount || 0 }}/9
              </span>
            </div>
            <div class="info-item">
              <label>状态:</label>
              <span class="status-badge" :class="getStatusClass(currentActiveAccount.status)">
                {{ getAccountStatusText(currentActiveAccount.status) }}
              </span>
            </div>
          </div>
          <div class="account-actions">
            <button
              v-if="(currentActiveAccount.activatedUsersCount || 0) >= 9"
              @click="switchToNextAvailableAccount"
              class="btn btn-warning"
              :disabled="!hasAvailableAccounts"
            >
              切换到下一个可用账户
            </button>
          </div>
        </div>
      </section>

      <div v-if="message" class="message" :class="messageType">
        {{ message }}
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '@/components/admin/AdminLayout.vue'
import AdminTable from '@/components/admin/AdminTable.vue'
import AdminPagination from '@/components/admin/AdminPagination.vue'
import {
  getAdminAccountPage,
  createAdminAccount,
  deleteAdminAccount,
  batchDeleteAdminAccounts,
  setActiveAccount,
  switchToNextAvailableAccount,
  bulkImportAdminAccounts,
  exportAdminAccounts,
  downloadAdminTemplate
} from '@/api/config'

export default {
  name: 'ConfigManagement',
  components: {
    AdminLayout,
    AdminTable,
    AdminPagination
  },
  data() {
    return {
      loading: false,
      message: '',
      messageType: 'success',
      refreshInterval: null,
      newAdmin: {
        email: '',
        password: ''
      },
      showNewPassword: false,
      currentPage: 1,
      pageSize: 10,
      searchKeyword: '',
      selectedAccounts: [],
      adminAccounts: [],
      totalAccounts: 0,
      isMobile: false,
      tableColumns: [
        { key: 'checkbox', title: '', width: '50px' },
        { key: 'email', title: '管理员邮箱' },
        { key: 'password', title: '密码' },
        { key: 'activatedUsers', title: '已激活用户数' },
        { key: 'status', title: '状态' },
        { key: 'lastUsed', title: '最后使用时间' },
        { key: 'actions', title: '操作' }
      ]
    }
  },
  computed: {
    // 删除重复分页逻辑，直接使用后端分页数据
    filteredAccounts() {
      return this.adminAccounts // 后端已经处理了搜索和分页
    },
    currentActiveAccount() {
      return this.adminAccounts.find(account => account.isActive)
    },
    hasAvailableAccounts() {
      return this.adminAccounts.some(account =>
        !account.isActive && this.getStatusClass(account.status) === 'status-available'
      )
    },
    allSelected: {
      get() {
        const selectableAccounts = this.adminAccounts.filter(account => !account.isActive)
        return selectableAccounts.length > 0 &&
               selectableAccounts.every(account =>
                 this.selectedAccounts.includes(this.getAccountId(account, this.adminAccounts.indexOf(account)))
               )
      },
      set() {
        // This setter is handled by selectAllAccounts method
      }
    }
  },
  mounted() {
    this.loadConfig()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)

    // 每30秒自动刷新一次数据
    this.refreshInterval = setInterval(() => {
      this.loadConfig()
    }, 30000)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
    // 清理定时器
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    async loadConfig() {
      this.loading = true
      try {
        const response = await getAdminAccountPage({
          current: this.currentPage,
          size: this.pageSize,
          email: this.searchKeyword,
          accountType: 'config'  // 只加载config类型的管理员账户，过滤掉export类型
        })

        if (response.code === 200) {
          this.adminAccounts = (response.data.records || []).map(account => ({
            ...account,
            showPassword: false
          }))
          this.totalAccounts = response.data.total || 0
          this.showMessage('配置加载成功', 'success')
        } else {
          this.showMessage(response.message || '加载配置失败', 'error')
        }
      } catch (error) {
        this.showMessage('加载配置失败: ' + error.message, 'error')
        console.warn('API不可用，请检查后端服务')
      } finally {
        this.loading = false
      }
    },

    async handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      if (!file.name.match(/\.(xlsx|xls)$/)) {
        this.showMessage('请选择Excel文件 (.xlsx或.xls)', 'error')
        return
      }

      this.loading = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('importedBy', 'admin')

        const response = await bulkImportAdminAccounts(formData)
        if (response.code === 200) {
          this.showMessage(response.message, 'success')
          this.loadConfig()
        } else {
          this.showMessage(response.message || '导入失败', 'error')
        }
      } catch (error) {
        this.showMessage('文件导入失败: ' + error.message, 'error')
      } finally {
        this.loading = false
        event.target.value = ''
      }
    },

    selectAllAccounts() {
      const selectableAccounts = this.adminAccounts.filter(account => !account.isActive)

      if (this.allSelected) {
        selectableAccounts.forEach(account => {
          const id = this.getAccountId(account, this.adminAccounts.indexOf(account))
          const index = this.selectedAccounts.indexOf(id)
          if (index > -1) {
            this.selectedAccounts.splice(index, 1)
          }
        })
      } else {
        selectableAccounts.forEach(account => {
          const id = this.getAccountId(account, this.adminAccounts.indexOf(account))
          if (!this.selectedAccounts.includes(id)) {
            this.selectedAccounts.push(id)
          }
        })
      }
    },

    async deleteSelected() {
      if (this.selectedAccounts.length === 0) return

      if (confirm(`确定要删除选中的 ${this.selectedAccounts.length} 个账户吗？`)) {
        try {
          const response = await batchDeleteAdminAccounts(this.selectedAccounts)
          if (response.code === 200) {
            this.selectedAccounts = []
            this.showMessage('批量删除成功', 'success')
            this.loadConfig()
          } else {
            this.showMessage(response.message || '批量删除失败', 'error')
          }
        } catch (error) {
          this.showMessage('批量删除失败: ' + error.message, 'error')
        }

        if (this.adminAccounts.length === 0 && this.currentPage > 1) {
          this.currentPage--
          this.loadConfig()
        }
      }
    },

    getAccountId(account, index) {
      return account.id || (this.currentPage - 1) * this.pageSize + index
    },

    getGlobalIndex(pageIndex) {
      const account = this.adminAccounts[pageIndex]
      return this.adminAccounts.findIndex(a =>
        (a.id || this.adminAccounts.indexOf(a)) === (account.id || pageIndex)
      )
    },

    getRowClassName(item) {
      return item.isActive ? 'highlighted' : ''
    },

    async addAdmin() {
      if (!this.newAdmin.email || !this.newAdmin.password) {
        this.showMessage('请填写完整的邮箱和密码', 'error')
        return
      }

      try {
        const response = await createAdminAccount({
          email: this.newAdmin.email,
          password: this.newAdmin.password,
          maxActivationLimit: 9,
          status: 1,
          isActive: this.adminAccounts.length === 0
        })

        if (response.code === 200) {
          this.newAdmin = { email: '', password: '' }
          this.showMessage('管理员账户添加成功', 'success')
          alert('管理员账户添加成功')
          this.loadConfig()
        } else {
          this.showMessage(response.message || '添加失败', 'error')
          alert('添加失败: ' + (response.message || '添加失败'))
        }
      } catch (error) {
        this.showMessage('添加失败: ' + error.message, 'error')
        alert('添加失败: ' + error.message)
      }
    },

    async removeAdmin(index) {
      const admin = this.adminAccounts[index]
      if (admin.isActive) {
        this.showMessage('无法删除当前激活的账户', 'error')
        return
      }

      try {
        const response = await deleteAdminAccount(admin.id)
        if (response.code === 200) {
          this.adminAccounts.splice(index, 1)
          this.showMessage('管理员账户删除成功', 'success')
        } else {
          this.showMessage(response.message || '删除失败', 'error')
        }
      } catch (error) {
        this.showMessage('删除失败: ' + error.message, 'error')
      }
    },

    async setActiveAccount(index) {
      const admin = this.adminAccounts[index]

      try {
        const response = await setActiveAccount(admin.id)
        if (response.code === 200) {
          this.adminAccounts.forEach(account => {
            account.isActive = false
          })
          this.adminAccounts[index].isActive = true
          this.showMessage('已切换当前激活账户', 'success')
        } else {
          this.showMessage(response.message || '设置失败', 'error')
        }
      } catch (error) {
        this.showMessage('设置失败: ' + error.message, 'error')
      }
    },

    async switchToNextAvailableAccount() {
      try {
        const response = await switchToNextAvailableAccount('config')  // 只在config类型账户中切换
        if (response.code === 200) {
          this.showMessage('已自动切换到下一个可用账户', 'success')
          this.loadConfig()
        } else {
          this.showMessage(response.message || '切换失败', 'error')
        }
      } catch (error) {
        this.showMessage('切换失败: ' + error.message, 'error')
      }
    },

    downloadTemplate() {
      downloadAdminTemplate().then(response => {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = 'biorender_admin_template.xlsx'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        this.showMessage('模板下载成功', 'success')
      }).catch(error => {
        this.showMessage('模板下载失败: ' + error.message, 'error')
      })
    },

    exportAccounts() {
      exportAdminAccounts().then(response => {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = `biorender_accounts_${new Date().toISOString().split('T')[0]}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        this.showMessage('账户数据导出成功', 'success')
      }).catch(error => {
        this.showMessage('导出失败: ' + error.message, 'error')
      })
    },

    togglePassword(index) {
      this.adminAccounts[index].showPassword = !this.adminAccounts[index].showPassword
    },

    getStatusClass(status) {
      const statusClassMap = {
        1: 'status-available',
        2: 'status-full',
        3: 'status-disabled',
        4: 'status-error',
        'available': 'status-available',
        'full': 'status-full',
        'disabled': 'status-disabled',
        'error': 'status-error'
      }
      return statusClassMap[status] || 'status-available'
    },

    getAccountStatusText(status) {
      const statusMap = {
        1: '可用',
        2: '已满',
        3: '已禁用',
        4: '异常',
        'available': '可用',
        'full': '已满',
        'disabled': '已禁用',
        'error': '异常'
      }
      return statusMap[status] || '可用'
    },

    showMessage(text, type = 'success') {
      this.message = text
      this.messageType = type
      setTimeout(() => {
        this.message = ''
      }, 3000)
    }
  }
}
</script>

<style scoped>
@import '@/styles/admin-common.css';

.config-management h2 {
  text-align: center;
  font-size: var(--font-size-xxl);
  margin-bottom: var(--spacing-xxl);
  color: var(--text-primary);
}

.config-management h3 {
  font-size: var(--font-size-xl);
  border-bottom: 2px solid var(--border-color);
  padding-bottom: var(--spacing-sm);
  margin-bottom: var(--spacing-xl);
  color: var(--text-primary);
}

.add-admin-section {
  margin-bottom: var(--spacing-xxl);
}

.bulk-import-section {
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-xl);
  background: var(--bg-light);
  border: 1px solid #b3d9ff;
  border-radius: var(--border-radius-md);
}

.import-actions {
  margin-bottom: var(--spacing-md);
}

.import-buttons-row {
  display: flex;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
  flex-wrap: wrap;
}

.import-buttons-row:last-child {
  margin-bottom: 0;
}

.add-admin-form {
  background: var(--bg-primary);
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
}

.password-input {
  position: relative;
}

.password-toggle,
.password-toggle-table {
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--font-size-lg);
  padding: var(--spacing-xs);
  opacity: 0.7;
  transition: opacity var(--transition-fast);
  flex-shrink: 0;
}

.password-toggle:hover,
.password-toggle-table:hover {
  opacity: 1;
}

.password-display {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 150px;
}

.password-display span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-count {
  font-weight: 600;
  color: var(--success-color);
}

.user-count.full {
  color: var(--danger-color);
}

.current-account-info {
  margin-bottom: var(--spacing-xxl);
}

.current-account-card {
  background: #e3f2fd;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-xl);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-xl);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.info-item label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  font-weight: 600;
  margin-bottom: 0;
}

.info-item span {
  font-weight: 500;
}

/* 移动端卡片样式 */
.mobile-accounts {
  display: block;
}

.mobile-loading, .mobile-empty {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-secondary);
  font-style: italic;
}

.mobile-account-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.mobile-account-item {
  background: var(--bg-primary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.mobile-account-item.highlighted {
  background: #e3f2fd;
  border-color: var(--primary-color);
}

.mobile-account-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--border-light);
}

.account-email {
  font-weight: 600;
  color: var(--text-primary);
  font-size: var(--font-size-md);
}

.mobile-account-content {
  margin-bottom: var(--spacing-md);
}

.mobile-account-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xs) 0;
  border-bottom: 1px solid var(--border-lighter);
}

.mobile-account-row:last-child {
  border-bottom: none;
}

.mobile-account-row .label {
  font-weight: 600;
  color: var(--text-secondary);
  flex: 0 0 auto;
  min-width: 80px;
}

.mobile-account-row .password-display {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.password-toggle-mobile {
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--font-size-sm);
  padding: var(--spacing-xxs);
  opacity: 0.7;
  transition: opacity var(--transition-fast);
}

.password-toggle-mobile:hover {
  opacity: 1;
}

.checkbox-label {
  margin-left: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.mobile-account-actions {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
}

.mobile-account-actions .btn {
  flex: 1;
  max-width: 100px;
}

@media (max-width: 768px) {
  .add-admin-form .flex {
    flex-direction: column;
    align-items: stretch;
  }

  .current-account-card {
    flex-direction: column;
    align-items: stretch;
  }

  .account-info {
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  .list-controls {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
  }

  .search-box input {
    width: 100% !important;
  }
}
</style>